import React from 'react';
import { Form, Switch } from 'antd';
import { AppstoreOutlined, ArrowRightOutlined, InboxOutlined, BorderOuterOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { PACKAGING_BOUNDS } from '../../../utils/transportCostAllocation';
import {
  PALLET_PRESETS,
  calculatePalletSummary,
  calculatePieceMetrics,
  matchPalletPreset,
  type PalletPresetId,
  type ProductLogisticsInput,
} from '../productLogistics';
import {
  CardNumberField,
  MAX_WEIGHT_KG,
  integerRangeRule,
  nonNegativeNumberRule,
  positiveNumberRule,
} from './cardFields';
import { formatReadout } from './cardFormat';
import CardSectionHeader from './CardSectionHeader';

interface PalletSectionProps {
  values: ProductLogisticsInput;
  disabled: boolean;
  /** Pallet preset chosen explicitly this session (null = derive from sizes). */
  preset: PalletPresetId | null;
  onPresetChange: (preset: PalletPresetId) => void;
}

const PRESET_LABEL_KEYS: Record<PalletPresetId, string> = {
  eur: 'products:card.pallet.presetEur',
  industrial: 'products:card.pallet.presetIndustrial',
  custom: 'products:card.pallet.presetCustom',
};

/** "Pakowanie i paleta": Sztuka ×N → Paczka (optional) ×M → Paleta + pallet size. */
const PalletSection: React.FC<PalletSectionProps> = ({ values, disabled, preset, onPresetChange }) => {
  const { t } = useTranslation(['products']);
  const piece = calculatePieceMetrics(values);
  const pallet = calculatePalletSummary(values);
  const packed = values.palletCountsPackages === true;
  const perPackage = Number(values.piecesPerPackage) > 0 ? Number(values.piecesPerPackage) : null;
  const units = Number(values.palletUnitsPerPallet) > 0 ? Number(values.palletUnitsPerPallet) : null;
  const activePreset = preset ?? matchPalletPreset(values.palletLength, values.palletWidth);

  const totals = (pieces: number | null, kg: number | null, m2: number | null) => (
    <span className="product-card-node__line">
      {pieces !== null && <b>{formatReadout(pieces, 3)} szt.</b>}
      {[
        m2 ? `${formatReadout(m2, 2)} m²` : null,
        kg ? `${formatReadout(kg, kg < 10 ? 2 : 0)} kg` : null,
      ].filter(Boolean).map((text) => ` · ${text}`).join('')}
    </span>
  );

  return (
    <section className="product-card-section">
      <CardSectionHeader
        title={t('products:card.pallet.title')}
        subtitle={t('products:card.pallet.subtitle')}
        hint={t('products:card.pallet.hint')}
      />
      <div className="product-card-flow">
        <div className="product-card-node">
          <div className="product-card-node__head">
            <span className="product-card-node__icon" aria-hidden="true"><BorderOuterOutlined /></span>
            <div>
              <div className="product-card-node__title">{t('products:card.pallet.piece')}</div>
              <div className="product-card-node__sub">{t('products:card.pallet.pieceSub')}</div>
            </div>
          </div>
          <div className="product-card-node__total" data-testid="pallet-node-piece">
            {piece.weightKg || piece.areaM2
              ? totals(1, piece.weightKg, piece.areaM2)
              : <span className="product-card-missing">{t('products:card.pallet.fillPiece')}</span>}
          </div>
        </div>

        <div className="product-card-link" aria-hidden="true">
          <span className={packed ? 'product-card-link__times' : 'product-card-link__times is-none'}>
            {packed ? (perPackage ? `× ${formatReadout(perPackage, 3)}` : '× ?') : t('products:card.pallet.noPackages')}
          </span>
          <ArrowRightOutlined />
        </div>

        <div className={packed ? 'product-card-node' : 'product-card-node is-off'}>
          <div className="product-card-node__head">
            <span className="product-card-node__icon" aria-hidden="true"><InboxOutlined /></span>
            <div>
              <div className="product-card-node__title">{t('products:card.pallet.package')}</div>
              <div className="product-card-node__sub">
                {packed ? t('products:card.pallet.packageSubOn') : t('products:card.pallet.packageSubOff')}
              </div>
            </div>
          </div>
          <label className="product-card-switch">
            <Form.Item name="palletCountsPackages" valuePropName="checked" noStyle>
              <Switch size="small" disabled={disabled} aria-label={t('products:card.pallet.packedToggle')} />
            </Form.Item>
            <span>{t('products:card.pallet.packedToggle')}</span>
          </label>
          {packed && (
            <CardNumberField
              name="piecesPerPackage"
              label={t('products:card.pallet.piecesPerPackage')}
              suffix="szt."
              disabled={disabled}
              rules={[
                { required: true, message: t('products:card.pallet.howManyInPackage') },
                positiveNumberRule(t, PACKAGING_BOUNDS.maxPiecesPerPackage),
              ]}
            />
          )}
          <div className="product-card-node__total" data-testid="pallet-node-package">
            {packed
              ? (perPackage
                ? totals(perPackage, piece.weightKg ? perPackage * piece.weightKg : null, piece.areaM2 ? perPackage * piece.areaM2 : null)
                : <span className="product-card-missing">{t('products:card.pallet.howManyInPackage')}</span>)
              : <span className="product-card-muted">{t('products:card.pallet.loose')}</span>}
          </div>
        </div>

        <div className="product-card-link" aria-hidden="true">
          <span className="product-card-link__times">{units ? `× ${formatReadout(units, 3)}` : '× ?'}</span>
          <ArrowRightOutlined />
        </div>

        <div className="product-card-node">
          <div className="product-card-node__head">
            <span className="product-card-node__icon" aria-hidden="true"><AppstoreOutlined /></span>
            <div>
              <div className="product-card-node__title">{t('products:card.pallet.pallet')}</div>
              <div className="product-card-node__sub">{t('products:card.pallet.palletSub')}</div>
            </div>
          </div>
          <CardNumberField
            name="palletUnitsPerPallet"
            label={packed ? t('products:card.pallet.packagesPerPallet') : t('products:card.pallet.piecesPerPallet')}
            suffix={packed ? t('products:card.pallet.unitPackages') : 'szt.'}
            disabled={disabled}
            rules={[positiveNumberRule(t, PACKAGING_BOUNDS.maxPiecesPerPackage)]}
          />
          <div className="product-card-node__total" data-testid="pallet-node-pallet">
            {pallet.pieces
              ? (
                <>
                  {totals(pallet.pieces, pallet.grossKg, pallet.areaM2)}
                  {pallet.ldm && <span>{t('products:card.pallet.ldmOnTrailer', { value: formatReadout(pallet.ldm, 2) })}</span>}
                </>
              )
              : <span className="product-card-missing">{t('products:card.pallet.howManyOnPallet')}</span>}
          </div>
        </div>
      </div>

      <div className="product-card-pallet-panel">
        <h4>{t('products:card.pallet.size')}</h4>
        <div className="product-card-presets" role="group" aria-label={t('products:card.pallet.size')}>
          {PALLET_PRESETS.map((candidate) => (
            <button
              key={candidate.id}
              type="button"
              disabled={disabled}
              aria-pressed={activePreset === candidate.id}
              className={activePreset === candidate.id ? 'product-card-chip is-on' : 'product-card-chip'}
              onClick={() => onPresetChange(candidate.id)}
            >
              {t(PRESET_LABEL_KEYS[candidate.id])}
            </button>
          ))}
        </div>
        <div className="product-card-grid">
          <CardNumberField name="palletLength" label={t('products:card.pallet.palletLength')} suffix="mm" disabled={disabled}
            rules={[positiveNumberRule(t, PACKAGING_BOUNDS.maxPackageDimensionMm)]} />
          <CardNumberField name="palletWidth" label={t('products:card.pallet.palletWidth')} suffix="mm" disabled={disabled}
            rules={[positiveNumberRule(t, PACKAGING_BOUNDS.maxPackageDimensionMm)]} />
          <CardNumberField name="palletEmptyWeight" label={t('products:card.pallet.emptyWeight')} suffix="kg" disabled={disabled}
            rules={[nonNegativeNumberRule(t, MAX_WEIGHT_KG)]} />
          <CardNumberField name="palletStackLayers" label={t('products:card.pallet.stack')} suffix="szt." disabled={disabled}
            placeholder="1"
            rules={[integerRangeRule(t, PACKAGING_BOUNDS.minStackLayers, PACKAGING_BOUNDS.maxStackLayers)]} />
        </div>
      </div>
    </section>
  );
};

export default PalletSection;
